import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBuildingState } from "../../../store/actions/permitFormActions/buildingStateActions";
import { privateProviderLabel } from "../../../data/buildingPermit/fieldDatas";
import { formikRef } from "../../../store/formikStore/formikStore";
import { LoadingButton } from "@mui/lab";
import {
  handleSaveProjects,
  pushNewPrivateProvider,
} from "../../../store/actions/dataActions";
import { TEMPLATE_CATEGORY_IDS } from "../../permitForms/utils/templateCategoryHelpers";

// Fixed set of Private Provider fields — always shown as-is in the
// "Add New Private Provider" modal, regardless of which document
// triggered it. Matches this document type's real field names/ids.
const PRIVATE_PROVIDER_FIELDS = [
  { name: "pp_company_name", field_id: "2a419639-8e9e-46ee-ad90-49a5f40cd93e" },
  { name: "pp_full_name", field_id: "b7e7f7c2-35b2-4b4d-afdb-329e6c136b60" },
  { name: "pp_full_address", field_id: "4163d761-9726-4905-ab42-5f35d2962358" },
  { name: "pp_phone", field_id: "092a288c-1b1b-4802-a7ad-c1f932bba039" },
  { name: "pp_fax", field_id: "ccd0da0b-f8aa-4327-a101-f2c55addae4f" },
  { name: "pp_email", field_id: "85a695ef-44b4-45c3-a47a-ecca1c78ac91" },
  { name: "pp_title", field_id: "31ce3c47-4f5f-4d57-a46c-1b09a56baf93" },
  { name: "pp_license_num", field_id: "b1b2046e-a183-45c4-b0d1-f342e3401a0f" },
];

const EMPTY_FIELD_VALUES = PRIVATE_PROVIDER_FIELDS.reduce(
  (values, field) => ({ ...values, [field.name]: "" }),
  {},
);

const REQUIRED_COMPANY_FIELD_NAME = "pp_company_name";

const PRIVATE_PROVIDER_FIELD_LABELS = {
  pp_company_name: "Private Provider Firm Name",
  pp_full_name: "Private Provider Full Name",
  pp_full_address: "Private Provider Full Address",
  pp_phone: "Private Provider Phone No.",
  pp_fax: "Private Provider Fax",
  pp_email: "Private Provider E-mail",
  pp_title: " Private Provider Title",
  pp_license_num: "Private Provider License Number",
};

const getFieldLabel = (field) => {
  const name = field?.name;
  return (
    PRIVATE_PROVIDER_FIELD_LABELS[name] ||
    privateProviderLabel?.[name] ||
    String(name || "")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase())
  );
};

const AddingNewPrivateProviderModal = () => {
  const formik = formikRef.current;
  const dispatch = useDispatch();

  const { addingNewPrivateProvider, privateProviderLoading } = useSelector(
    (state) => state.buildingState,
  );

  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [fieldValues, setFieldValues] = useState(EMPTY_FIELD_VALUES);

  useEffect(() => {
    if (!addingNewPrivateProvider) return;

    setFieldValues((prev) => ({ ...EMPTY_FIELD_VALUES, ...prev }));
  }, [addingNewPrivateProvider]);

  useEffect(() => {
    const companyName = String(fieldValues[REQUIRED_COMPANY_FIELD_NAME] ?? "");
    setSubmitDisabled(!companyName || companyName.trim().length < 3);
  }, [fieldValues]);

  const runFieldChange = (event) => {
    const { name, value } = event.target;

    setFieldValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneInputChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    if (value.length > 10) value = value.slice(0, 10);

    let formatted = "";
    if (value.length > 0) {
      if (value.length <= 3) {
        formatted = value;
      } else if (value.length <= 6) {
        formatted = `${value.slice(0, 3)}-${value.slice(3)}`;
      } else {
        formatted = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
      }
    }

    setFieldValues((prev) => ({
      ...prev,
      pp_phone: formatted,
    }));
  };

  const closeModal = () => {
    dispatch(
      setBuildingState({
        addingNewPrivateProvider: false,
        activeTemplateCategory: null,
      }),
    );
    setFieldValues(EMPTY_FIELD_VALUES);
  };

  const handleAddNew = async () => {
    try {
      await dispatch(setBuildingState({ privateProviderLoading: true }));

      const newID = Date.now().toString();
      const createdAt = Math.floor(Date.now() / 1000);

      const categoryFieldValues = PRIVATE_PROVIDER_FIELDS.reduce(
        (values, field) => ({
          ...values,
          [field.name]: fieldValues[field.name] ?? "",
        }),
        {},
      );

      const fieldValuesById = PRIVATE_PROVIDER_FIELDS.reduce(
        (values, field) => ({
          ...values,
          [field.field_id]: fieldValues[field.name] ?? "",
        }),
        {},
      );

      const savedProjectObj = {
        id: newID,
        ...categoryFieldValues,
        category_id: TEMPLATE_CATEGORY_IDS.PRIVATE_PROVIDER,
        field_values: fieldValuesById,
        created_at: createdAt,
      };

      await dispatch(pushNewPrivateProvider(savedProjectObj));
      await dispatch(handleSaveProjects());

      formik?.setValues((prevValues) => ({
        ...prevValues,
        privateProvider: newID,
        ...categoryFieldValues,
      }));

      dispatch(
        setBuildingState({
          addingNewPrivateProvider: false,
          activeTemplateCategory: null,
        }),
      );
      setFieldValues(EMPTY_FIELD_VALUES);
    } catch (e) {
      console.error("Error adding private provider:", e);
    } finally {
      await dispatch(setBuildingState({ privateProviderLoading: false }));
    }
  };

  return (
    <Modal
      open={addingNewPrivateProvider}
      onClose={closeModal}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 500 },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <Typography id="modal-title" variant="h6" component="h2" mb={2}>
          Add New Private Provider
        </Typography>

        {PRIVATE_PROVIDER_FIELDS.map((field, i) => (
          <TextField
            key={field.name + i}
            fullWidth
            margin="normal"
            variant="filled"
            label={getFieldLabel(field)}
            name={field.name}
            value={fieldValues[field.name] ?? ""}
            onChange={
              field.name === "pp_phone"
                ? handlePhoneInputChange
                : runFieldChange
            }
            required={field.name === REQUIRED_COMPANY_FIELD_NAME}
            error={
              field.name === REQUIRED_COMPANY_FIELD_NAME &&
              fieldValues[field.name] &&
              String(fieldValues[field.name]).trim().length < 3
            }
            helperText={
              field.name === REQUIRED_COMPANY_FIELD_NAME &&
              fieldValues[field.name] &&
              String(fieldValues[field.name]).trim().length < 3
                ? "Firm Name must be at least 3 characters"
                : ""
            }
          />
        ))}

        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Button onClick={closeModal}>Cancel</Button>
          <LoadingButton
            loading={privateProviderLoading}
            variant="contained"
            onClick={handleAddNew}
            disabled={submitDisabled}
          >
            Save
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(AddingNewPrivateProviderModal);
